import {
    Anchor,
    Box,
    Button,
    Group,
    Stack,
    Text,
    Title,
} from '@mantine/core';
import { IconArrowLeft } from '@tabler/icons-react';
import ReactMarkdown from 'react-markdown';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, useParams } from 'react-router-dom';
import remarkGfm from 'remark-gfm';

import { PageHeader } from '../components/page-header';
import {
    WIKI_ARTICLES,
    WIKI_GROUP_ORDER,
    getWikiArticle,
    wikiSlugForMdHref,
    type WikiGroup,
} from '../wiki/catalog';

const groupArticles = (group: WikiGroup) =>
    WIKI_ARTICLES.filter((a) => a.group === group);

const WikiIndex = () => {
    const { t } = useTranslation();

    return (
        <>
            <PageHeader
                title={t('pages.wiki.title')}
                subtitle={t('pages.wiki.subtitle')}
            />
            <Stack gap='xl'>
                {WIKI_GROUP_ORDER.map((group) => {
                    const articles = groupArticles(group);
                    if (!articles.length) return null;
                    return (
                        <Box key={group}>
                            <Title order={4} mb='sm'>
                                {t(`pages.wiki.groups.${group}`)}
                            </Title>
                            <Stack gap='xs'>
                                {articles.map((article) => (
                                    <Box
                                        key={article.slug}
                                        component={Link}
                                        to={`/wiki/${article.slug}`}
                                        p='md'
                                        style={{
                                            display: 'block',
                                            textDecoration: 'none',
                                            color: 'inherit',
                                            borderRadius: 12,
                                            border: '1px solid var(--mantine-color-gray-3)',
                                            background: 'var(--mantine-color-white)',
                                        }}
                                    >
                                        <Text fw={600} size='sm'>
                                            {t(`pages.wiki.articles.${article.titleKey}.title`)}
                                        </Text>
                                        <Text size='sm' c='dimmed' mt={4}>
                                            {t(`pages.wiki.articles.${article.summaryKey}.summary`)}
                                        </Text>
                                    </Box>
                                ))}
                            </Stack>
                        </Box>
                    );
                })}
            </Stack>
        </>
    );
};

const WikiArticleView = ({ slug }: { slug: string }) => {
    const { t } = useTranslation();
    const article = getWikiArticle(slug);

    if (!article) {
        return <Navigate to='/wiki' replace />;
    }

    return (
        <>
            <Group mb='md'>
                <Button
                    component={Link}
                    to='/wiki'
                    variant='subtle'
                    color='gray'
                    leftSection={<IconArrowLeft size={16} />}
                    size='compact-sm'
                >
                    {t('pages.wiki.back')}
                </Button>
            </Group>
            <Box
                className='wiki-md'
                maw={760}
                style={{
                    fontSize: 'var(--mantine-font-size-sm)',
                    lineHeight: 1.65,
                }}
            >
                <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                        a: ({ href, children }) => {
                            const wikiSlug = href ? wikiSlugForMdHref(href) : undefined;
                            if (wikiSlug) {
                                return (
                                    <Anchor component={Link} to={`/wiki/${wikiSlug}`}>
                                        {children}
                                    </Anchor>
                                );
                            }
                            // Docs not in the wiki catalog (hiring, strategy, …) — plain text
                            const file = href?.replace(/^\.\//, '').split('#')[0] ?? '';
                            if (file.endsWith('.md') || file.includes('.md')) {
                                return <Text span inherit>{children}</Text>;
                            }
                            if (href?.startsWith('#')) {
                                return <Anchor href={href}>{children}</Anchor>;
                            }
                            return (
                                <Anchor href={href} target='_blank' rel='noopener noreferrer'>
                                    {children}
                                </Anchor>
                            );
                        },
                        h1: ({ children }) => (
                            <Title order={2} mb='md'>
                                {children}
                            </Title>
                        ),
                        h2: ({ children }) => (
                            <Title order={3} mt='xl' mb='sm'>
                                {children}
                            </Title>
                        ),
                        h3: ({ children }) => (
                            <Title order={4} mt='lg' mb='xs'>
                                {children}
                            </Title>
                        ),
                        p: ({ children }) => (
                            <Text component='p' mb='sm' style={{ lineHeight: 1.65 }}>
                                {children}
                            </Text>
                        ),
                        li: ({ children }) => (
                            <Text component='li' mb={4} style={{ lineHeight: 1.65 }}>
                                {children}
                            </Text>
                        ),
                    }}
                >
                    {article.markdown}
                </ReactMarkdown>
            </Box>
            <style>{`
                .wiki-md table {
                    width: 100%;
                    border-collapse: collapse;
                    margin: 1em 0;
                    font-size: 0.92em;
                }
                .wiki-md th,
                .wiki-md td {
                    border: 1px solid var(--mantine-color-gray-3);
                    padding: 0.45em 0.65em;
                    text-align: left;
                    vertical-align: top;
                }
                .wiki-md th {
                    background: var(--mantine-color-gray-0);
                    font-weight: 600;
                }
                .wiki-md pre {
                    overflow-x: auto;
                    padding: 0.75em 1em;
                    border-radius: 8px;
                    background: var(--mantine-color-gray-0);
                    border: 1px solid var(--mantine-color-gray-2);
                }
                .wiki-md code {
                    font-size: 0.9em;
                }
                .wiki-md hr {
                    border: none;
                    border-top: 1px solid var(--mantine-color-gray-3);
                    margin: 1.5em 0;
                }
                .wiki-md ul,
                .wiki-md ol {
                    padding-left: 1.25rem;
                    margin: 0 0 0.75rem;
                }
                .wiki-md blockquote {
                    margin: 0.75rem 0;
                    padding: 0.25rem 0 0.25rem 0.85rem;
                    border-left: 3px solid var(--mantine-color-gray-3);
                    color: var(--mantine-color-dimmed);
                }
            `}</style>
        </>
    );
};

export const WikiPage = () => {
    const { slug } = useParams<{ slug?: string }>();
    if (slug) {
        return <WikiArticleView slug={slug} />;
    }
    return <WikiIndex />;
};
